// @Version: 1.0.3
import { Component, EventEmitter, Output, forwardRef, Input, OnDestroy, OnChanges, signal, SimpleChanges } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { ErrorMessagesService } from 'src/app/shared/services/errorMessages/error-messages.service';
import { ValidatorsService } from 'src/app/shared/services/validators/validators.service';
import { IErrorCodes, IFieldItem, ITEMTYPE, VALIDATORS } from '../field-item.model';
import { FC_EMAIL } from './email.model';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TRANSLATE_PATH } from 'src/app/shared/shared.model';
import { Subscription } from 'rxjs';

@UntilDestroy()
@Component({
   selector: 'app-field-item-email',
   templateUrl: './email.component.html',
   providers: [
      {
         provide: NG_VALUE_ACCESSOR,
         useExisting: forwardRef(() => FieldItemEmailComponent),
         multi: true
      },
      {
         provide: NG_VALIDATORS,
         useExisting: forwardRef(() => FieldItemEmailComponent),
         multi: true
      },
      ErrorMessagesService
   ],
   standalone: false
})
export class FieldItemEmailComponent implements ControlValueAccessor, Validators, OnChanges, OnDestroy {

   public FC_EMAIL: typeof FC_EMAIL = FC_EMAIL; 
   public VALIDATORS: typeof VALIDATORS = VALIDATORS;

   public done$$ =signal(false);

   public fgEmail: FormGroup = this.fb.group({});
   public hideRequireMaker: boolean = false;

   private fieldItem_: IFieldItem = {} as IFieldItem;
   private errorCodes_: IErrorCodes = {} as IErrorCodes;
   private sub_?: Subscription;

   @Input() clearValueIfDisabled: boolean = true;

   @Input() 
   get fieldItem (): IFieldItem {
     return this.fieldItem_;
   }
   set fieldItem (value: IFieldItem) {
      this.fieldItem_ = value || {};
   }

   @Input()
   get value (): string {
      return this.fgEmail.get(FC_EMAIL.EMAIL)?.value ?? '';
   }   

   @Output() isRequiredMarker: EventEmitter<boolean> = new EventEmitter<boolean>();
   
   get error (): IErrorCodes {
      return this.errorCodes_;
   }

   constructor(private fb: FormBuilder, private sErrorMessages: ErrorMessagesService, private sValidators: ValidatorsService) { }

   ngOnChanges(changes: SimpleChanges): void {
      if (changes && changes.fieldItem &&  (JSON.stringify(changes.fieldItem.currentValue) != JSON.stringify(changes.fieldItem.previousValue))) {
         this.errorCodes_ = this.sErrorMessages.setErrorCodes(changes.fieldItem.currentValue.attribute_2 ?? '', TRANSLATE_PATH.FIELD_ITEM_ERROR);
         this.initFormGroup(changes.fieldItem.currentValue);
         this.onValidationChange();
         this.done$$.set(true);
      }
   }

   ngOnDestroy (): void {
      this.reset();
   }
   
   validate (control: AbstractControl): ValidationErrors | null {
      return this.fgEmail.get(FC_EMAIL.EMAIL) ? this.fgEmail.get(FC_EMAIL.EMAIL)!.valid ? null : this.fgEmail.get(FC_EMAIL.EMAIL)!.errors : null;
   }

   writeValue (value: string | undefined | null): void {
      this.fgEmail.get(FC_EMAIL.EMAIL)?.setValue(value ? value.trim() : undefined);

      if (typeof value == 'string' && value.length > 0) this.setDirtyTouched();
      this.onValidationChange();
   }

   registerOnChange (fn: any): void {
      this.onChange = fn
   }

   registerOnTouched (fn: any): void { 
      this.onTouched = fn; 
   }

   private onTouched () {}

   private onChange (_: string) {}

   registerOnValidatorChange? (fn: () => void): void {
      this.onValidationChange = fn;
   }

   onValidationChange: any = () => {}; 

   setDisabledState (isDisabled: boolean | undefined): void { 
      if (this.fgEmail.get(FC_EMAIL.EMAIL)) isDisabled ? this.setDisabledControls() : this.fgEmail.get(FC_EMAIL.EMAIL)?.enable();
      this.onValidationChange();
   }

   private reset (): void {
      this.done$$.set(false);
      this.fgEmail = this.fb.group({});
      this.fieldItem_= {} as IFieldItem;
      this.errorCodes_ = {} as IErrorCodes;
      this.hideRequireMaker = false;
      this.isRequiredMarker.emit(false); 
   }

   private initFormGroup (fieldItem: IFieldItem): boolean {
      if (this.sub_) {
         this.sub_.unsubscribe();
         this.sub_ = undefined;
      }
      this.fgEmail = this.fb.group({
         [FC_EMAIL.EMAIL]: this.fb.control({value: fieldItem.content?.trim(), disabled: false}, {validators: this.sValidators.setValidators(this.fieldItem, ITEMTYPE.email), updateOn: 'change'})
      });

      this.sub_ = this.fgEmail.get(FC_EMAIL.EMAIL)?.valueChanges.pipe(
         debounceTime(500)
      ).subscribe(value => {
         if (!this.value && this.fgEmail.get(FC_EMAIL.EMAIL)?.pristine) return;
         if (value && value != value.trim())  this.fgEmail.get(FC_EMAIL.EMAIL)?.setValue(value.trim(), {emitEvent: false})
         this.onChange(this.value.trim());
         this.onTouched()
      })

      this.hideRequireMaker = fieldItem.attribute_1 && fieldItem.attribute_1 != '' ? false : true;
      this.isRequiredMarker.emit(fieldItem.required && !this.hideRequireMaker);

      return true;
   }

   private setDisabledControls (): void {
      this.fgEmail.get(FC_EMAIL.EMAIL)?.disable();
      if (this.clearValueIfDisabled) this.fgEmail.get(FC_EMAIL.EMAIL)?.setValue(undefined)
      this.fgEmail.get(FC_EMAIL.EMAIL)?.markAsTouched();
   }

   private setDirtyTouched (): void {
      this.fgEmail.get(FC_EMAIL.EMAIL)?.markAsDirty(); 
      this.fgEmail.get(FC_EMAIL.EMAIL)?.markAsTouched();
   }
}